import { getApolloClient } from '#admin/apollo.client.js';
import { CSRFProvider } from '#admin/contexts/CSRFContext.js';
import theme from '#admin/theme.js';
import { ApolloProvider } from '@apollo/client/react';
import createEmotionCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import * as SentryReact from '@sentry/react';
import React, { startTransition, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { HydratedRouter } from 'react-router/dom';

const emotionCache = createEmotionCache({ key: 'css', prepend: true });

if (ENV.SENTRY_RELEASE) {
  SentryReact.init({
    dsn: 'https://2e6378d0324584f229cbe007e23ef888@sentry.obvio.so/2',
    release: ENV.SENTRY_RELEASE,
    environment: ENV.SENTRY_ENVIRONMENT,
    integrations: [
      SentryReact.elementTimingIntegration(),
      SentryReact.viewHierarchyIntegration(),
      SentryReact.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // SentryReact.replayIntegration(),
    ],
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    tracePropagationTargets: ['localhost', /^\//],
    replaysSessionSampleRate: 0, // 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0, // 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    normalizeDepth: 5,
  });
}

function isInApp(): boolean {
  const rules = ['WebView', '(iPhone|iPod|iPad)(?!.*Safari/)', 'Android.*(wv)'];
  const regex = new RegExp(`(${rules.join('|')})`, 'ig');
  return Boolean(navigator.userAgent.match(regex));
}

export function fixReactVSBrowserExtensions() {
  // Fix browser extensions inserting code in <html></html>
  // https://github.com/facebook/react/issues/24430
  document
    .querySelectorAll('html > *:not(body, head), script[src*="extension://"], link[href*="extension://"]')
    .forEach((s) => {
      s.parentNode?.removeChild(s);
    });

  for (const child of Array.from(document.head.childNodes)) {
    // Remove everything before our first tag
    if ((child as HTMLElement).dataset?.first === 'true') {
      break;
    }
    child.remove();
  }

  if (isInApp()) {
    const contextScript = document.querySelector('script#context-script');
    if (contextScript) {
      contextScript.innerHTML = '';
    }
  }
}

// fixReactVSBrowserExtensions();

const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? null;

const apolloClient = getApolloClient();

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <CSRFProvider value={csrfToken}>
        <ApolloProvider client={apolloClient}>
          <CacheProvider value={emotionCache}>
            <ThemeProvider theme={theme(ENV.SENTRY_ENVIRONMENT)}>
              <CssBaseline />
              <HydratedRouter />
            </ThemeProvider>
          </CacheProvider>
        </ApolloProvider>
      </CSRFProvider>
    </StrictMode>,
  );
});
